// ─── « À la carte » — côté UTILISATEUR (Core) ──────────────────────
// Modèle pool de points, validé par Cédric le 19/08 : chaque forfait inclut
// un budget de points, chaque fonction/plafond individuel coûte un poids, et
// au-delà du budget un barème s'applique. L'utilisateur ne fait que DÉPOSER
// une demande (comme pour changer de forfait) — l'octroi réel reste un acte
// de l'administration (cf. MoP Admin, FeuilleCompte).
//
// Ne montre QUE ce qui reste à acheter : une fonction déjà incluse par le
// palier, ou déjà achetée, n'apparaît pas ici — la carte de forfait dit déjà
// ce qu'on a, ce panneau ne dit que ce qu'on PEUT ajouter.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { useAuth } from '../auth/AuthContext';
import { useFonctions } from './FonctionsContext';
import { fonctionInclusePlan, poidsDe } from './fonctionnalites';
import { TYPES_ELEMENTS } from './jetons';
import { pointsInclusDePlan, prixDePoints, aDemandeAchats, poidsSelection } from './pointsALaCarte';
import { ecouterBareme, demanderAchats, annulerDemandeAchats } from './pointsALaCarteService';
import Stepper from '../ui/Stepper';
import Bascule from '../ui/Bascule';

const MAX_QUOTA_DEMANDE = 9;   // borne d'interface, pas une règle métier — évite une saisie absurde.
const LABEL_QUOTA = {
  gestion: 'admin.jetonsGestion', vue: 'admin.jetonsVue', espaces: 'admin.maxEspaces',
  calendriers: 'admin.typeCalendriers', inventaires: 'admin.typeInventaires',
  repas: 'admin.typeRepas', comptes: 'admin.typeComptes',
};
const QUOTAS = ['gestion', 'vue', 'espaces', ...TYPES_ELEMENTS];

// Poids total d'un ensemble d'achats DÉJÀ EN PLACE (fonctions + quotas),
// même grammaire que la sélection en cours — un seul calcul pour les deux.
function poidsAchats(cfg, catalogueByKey, fonctions, quotas) {
  return poidsSelection({
    fonctions: Object.keys(fonctions || {}).map(cle => ({ poids: poidsDe(cfg, catalogueByKey[cle] || { key: cle }) })),
    quotas: Object.fromEntries(Object.entries(quotas || {}).map(([cle, n]) => [cle, { n, poids: poidsDe(cfg, { key: cle }) }])),
  });
}

export default function SectionALaCarte() {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user } = useAuth();
  const { catalogue, config } = useFonctions();
  const [bareme, setBareme] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [selFonctions, setSelFonctions] = useState({});   // { cle: true }
  const [selQuotas, setSelQuotas] = useState({});          // { cle: n }

  useEffect(() => ecouterBareme(setBareme, (e) => setErr(e?.message || String(e))), []);

  const plan = user?.plan || 'gratuit';
  const palierInclus = config.plans?.[plan]?.palierInclus;
  const achatsFn = user?.fonctionnalitesAchetees || {};
  const achatsQuo = user?.quotasAchetes || {};
  const pointsInclus = pointsInclusDePlan(config, plan);
  const demande = aDemandeAchats(user) ? user.achatsDemande : null;

  // Fonctions PAS déjà incluses par le palier ET pas déjà achetées.
  const achetables = useMemo(() => catalogue.list.filter(d => d.individuel
    && !fonctionInclusePlan(d, palierInclus, achatsFn, config.paliers)), [catalogue, palierInclus, achatsFn, config.paliers]);

  const poidsExistants = poidsAchats(config, catalogue.byKey, achatsFn, achatsQuo);
  const poidsSelectionCourante = poidsAchats(config, catalogue.byKey, selFonctions, selQuotas);
  const total = poidsExistants + poidsSelectionCourante;
  const overageAvant = Math.max(0, poidsExistants - pointsInclus);
  const overageApres = Math.max(0, total - pointsInclus);
  const prixAvant = prixDePoints(bareme, overageAvant);
  const prixApres = prixDePoints(bareme, overageApres);
  const rienSelectionne = Object.keys(selFonctions).length === 0 && Object.keys(selQuotas).length === 0;

  const envoyer = async () => {
    if (rienSelectionne || busy || !user?.uid) return;
    setBusy(true); setErr('');
    try {
      await demanderAchats(user.uid, { fonctions: Object.keys(selFonctions), quotas: selQuotas });
      setSelFonctions({}); setSelQuotas({});
    } catch (e) { setErr(e?.message || String(e)); }
    setBusy(false);
  };
  const annuler = () => { setBusy(true); annulerDemandeAchats(user.uid).catch((e) => setErr(e?.message || String(e))).finally(() => setBusy(false)); };

  if (demande) {
    return (
      <View>
        {!!err && <Text style={s.err}>⚠️ {err}</Text>}
        <TouchableOpacity style={s.demande} onPress={annuler} activeOpacity={0.7} disabled={busy}>
          <Text style={s.demandeTxt}>⏳ {t('forfait.demandeAchatsEnCours')}</Text>
          <Text style={s.demandeAnnul}>{t('commun.annuler')}</Text>
        </TouchableOpacity>
        <Text style={s.aide}>{t('forfait.demandeAchatsAide')}</Text>
      </View>
    );
  }

  return (
    <View>
      {!!err && <Text style={s.err}>⚠️ {err}</Text>}
      <Text style={s.aide}>{t('forfait.aLaCarteIntro', { n: pointsInclus })}</Text>

      {achetables.length === 0 ? (
        <Text style={s.vide}>{t('forfait.aLaCarteAucune')}</Text>
      ) : achetables.map(def => (
        <View key={def.key} style={s.ligne}>
          <View style={s.ligneTxt}>
            <Text style={s.icone}>{def.icone || '🔹'}</Text>
            <View style={{ flex: 1 }}>
              <Text style={s.nom} numberOfLines={1}>{def.label || def.key}</Text>
              {!!def.desc && <Text style={s.desc} numberOfLines={1}>{def.desc}</Text>}
            </View>
          </View>
          <Text style={s.poids}>{poidsDe(config, def)} {t('admin.pts')}</Text>
          <Bascule valeur={!!selFonctions[def.key]} accessibilityLabel={def.label || def.key}
            onChange={(v) => setSelFonctions(m => ({ ...m, [def.key]: v || undefined }))} />
        </View>
      ))}

      <Text style={[s.aide, { marginTop: 10 }]}>{t('forfait.aLaCarteQuotasIntro')}</Text>
      {QUOTAS.map(cle => {
        const n = selQuotas[cle] || 0;
        return (
          <View key={cle} style={s.ligne}>
            <Text style={s.ligneTxt2}>🎟️ {t(LABEL_QUOTA[cle])} · {poidsDe(config, { key: cle })} {t('admin.pts')}/u.</Text>
            <Stepper valeur={n} onMoins={() => setSelQuotas(m => ({ ...m, [cle]: Math.max(0, n - 1) || undefined }))}
              onPlus={() => setSelQuotas(m => ({ ...m, [cle]: Math.min(MAX_QUOTA_DEMANDE, n + 1) }))} />
          </View>
        );
      })}

      <View style={s.cart}>
        <Text style={s.cartTxt}>
          {t('forfait.aLaCartePoints', { n: total, inclus: pointsInclus })}
        </Text>
        <Text style={s.cartPrix}>
          {prixAvant.toFixed(2).replace('.', ',')} € → {prixApres.toFixed(2).replace('.', ',')} €{t('forfait.parMois')}
        </Text>
      </View>
      <TouchableOpacity style={[s.envoyer, rienSelectionne && s.envoyerOff]} disabled={rienSelectionne || busy} onPress={envoyer}>
        <Text style={[s.envoyerTxt, rienSelectionne && s.envoyerTxtOff]}>{t('forfait.envoyerDemande')}</Text>
      </TouchableOpacity>
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  aide: { fontSize: 11.5, color: UI.faint, lineHeight: 16, marginBottom: 8 },
  vide: { fontSize: 12.5, color: UI.faint, fontStyle: 'italic', marginBottom: 6 },
  err: { fontSize: 12.5, color: UI.danger, fontWeight: '700', marginBottom: 8 },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 9, paddingVertical: 8,
           borderTopWidth: 0.5, borderTopColor: UI.border },
  ligneTxt: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 },
  ligneTxt2: { flex: 1, fontSize: 13, fontWeight: '600', color: UI.ink },
  icone: { fontSize: 17 },
  nom: { fontSize: 13.5, fontWeight: '700', color: UI.ink },
  desc: { fontSize: 11, color: UI.faint, marginTop: 1 },
  poids: { fontSize: 11.5, fontWeight: '800', color: UI.accent, backgroundColor: UI.accentTint,
           borderRadius: 999, paddingHorizontal: 8, paddingVertical: 3 },
  cart: { backgroundColor: UI.champ, borderRadius: 11, padding: 11, marginTop: 12, gap: 3 },
  cartTxt: { fontSize: 12.5, color: UI.ink, fontWeight: '700' },
  cartPrix: { fontSize: 16, color: UI.accent, fontWeight: '900' },
  envoyer: { backgroundColor: UI.accent, borderRadius: 11, paddingVertical: 11, alignItems: 'center', marginTop: 10 },
  envoyerOff: { backgroundColor: UI.champ },
  envoyerTxt: { color: '#fff', fontSize: 13.5, fontWeight: '800' },
  envoyerTxtOff: { color: UI.muted },
  demande: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.champ,
             borderRadius: 11, borderWidth: 1, borderColor: UI.accent, padding: 11, marginBottom: 6 },
  demandeTxt: { flex: 1, fontSize: 13, color: UI.ink, fontWeight: '700' },
  demandeAnnul: { fontSize: 12, color: UI.accent, fontWeight: '800' },
});
